/**
 * App mode — the dashboard runs in two contexts from one codebase:
 *
 *  - "embedded": inside the Shopify admin (App Bridge present). Auth = Shopify
 *    session token; API base = /api; one store (the host shop).
 *  - "portal": the standalone site at PORTAL_URL. Auth = session cookie; API
 *    base = /portal/store; multi-store via an active-store id header.
 *
 * We detect embedded by the presence of the App Bridge global (or a `host`
 * query param Shopify always adds). Everything else is portal mode.
 */

export type AppMode = 'embedded' | 'portal';

export function detectMode(): AppMode {
  const hasBridge = Boolean((globalThis as { shopify?: { idToken?: unknown } }).shopify?.idToken);
  const hasHost = new URLSearchParams(window.location.search).has('host');
  return hasBridge || hasHost ? 'embedded' : 'portal';
}

export const APP_MODE: AppMode = detectMode();
export const IS_PORTAL = APP_MODE === 'portal';
export const IS_EMBEDDED = APP_MODE === 'embedded';

// ── Active store (portal multi-store) ────────────────────────────────────────

const ACTIVE_STORE_KEY = 'cs_active_store';

export function getActiveStoreId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_STORE_KEY);
  } catch {
    return null;
  }
}

export function setActiveStoreId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_STORE_KEY, id);
    else localStorage.removeItem(ACTIVE_STORE_KEY);
  } catch {
    /* storage unavailable — header simply won't be sent */
  }
}

/**
 * Rewrite an `/api/...` path to its portal equivalent when in portal mode, so
 * every existing hook (written against /api) transparently targets the portal
 * API. Non-/api paths (e.g. /auth/portal/*) pass through untouched.
 */
export function resolvePath(path: string): string {
  if (IS_EMBEDDED) return path;
  if (path.startsWith('/api/')) return `/portal/store/${path.slice('/api/'.length)}`;
  return path;
}
